import { http } from '@/api/http';

/**
 * Raw transport for the global-plane federated-identity admin surface (PTV2-1451, capture-then-link).
 * A platform admin lists captured external identities by status, links a PENDING one to an existing
 * global user, or unlinks (retires) one. Backed by {@code /api/v1/admin/federated-identities}; every
 * endpoint is {@code platform:MANAGE} server-side (PTV2-1383 — the global plane surfaces as
 * {@code platform:*}).
 *
 * Hand-written slice DTOs (snake_case, matching the backend {@code @JsonProperty} names) — the api-layer
 * mapper normalises to camelCase view models.
 */

/** Lifecycle of a federated identity. PENDING = captured, awaiting an admin link; RETIRED = unlinked. */
export type FederatedIdentityStatusDto = 'ACTIVE' | 'PENDING' | 'RETIRED';

/** Wire DTO for a federated identity (external subject → internal user). */
export interface FederatedIdentityDto {
  /** The identity's own guid (the path segment for link/unlink). */
  guid?: string;
  /** The linked internal user's guid; absent/blank for a PENDING capture. */
  user_guid?: string | null;
  /** Resolved display name of the linked user (server-populated); absent for a PENDING capture. */
  user_name?: string | null;
  /** The provider's issuer URI (the identity key, with the external subject). */
  provider_issuer?: string;
  /** The opaque external subject the IdP asserts (e.g. {@code auth0|abc123}). */
  external_subject?: string;
  status?: FederatedIdentityStatusDto;
  /** Captured display hint — the email the IdP asserted at capture time. */
  captured_email?: string | null;
  /** Captured display hint — the display name the IdP asserted at capture time. */
  captured_name?: string | null;
}

/** Request body for {@code POST /{guid}/link}. */
export interface LinkFederatedIdentityRequestDto {
  user_guid: string;
}

function encode(id: string): string {
  return encodeURIComponent(id);
}

export const adminFederatedIdentitiesEndpoints = {
  /**
   * GET /admin/federated-identities — identities by lifecycle state (`status`, default PENDING server-side)
   * or by linked user (`userGuid`, all of that user's identities). Pass whichever filter applies.
   */
  getFederatedIdentities: (
    params: { status?: FederatedIdentityStatusDto; userGuid?: string },
    options?: { signal?: AbortSignal },
  ): Promise<FederatedIdentityDto[]> =>
    http.get<FederatedIdentityDto[]>('/admin/federated-identities', {
      ...options,
      query: {
        ...(params.status !== undefined ? { status: params.status } : {}),
        ...(params.userGuid !== undefined ? { userGuid: params.userGuid } : {}),
      },
    }),

  /** POST /admin/federated-identities/{guid}/link — attach a captured identity to an existing user. */
  linkFederatedIdentity: (
    guid: string,
    body: LinkFederatedIdentityRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<FederatedIdentityDto> =>
    http.post<FederatedIdentityDto>(`/admin/federated-identities/${encode(guid)}/link`, body, options),

  /** POST /admin/federated-identities/{guid}/unlink — deactivate (retire) an identity. Returns 204. */
  unlinkFederatedIdentity: (guid: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.post<undefined>(`/admin/federated-identities/${encode(guid)}/unlink`, undefined, options),
};
